import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { LogOut, MessageCircle, Send, Swords, Users, Trophy } from 'lucide-react';
import { rpsRoomStore } from '@/store/rpsRoomStore';
import { sessionStore } from '@/store/sessionStore';
import {
  useRpsRoomSocket,
  useRpsRoomJoin,
  useRpsRoomLeave,
  useRpsRoomReady,
  useRpsRoomChoose,
  useRpsRoomChat,
} from '@/lib/socket/useRpsRoomSocket';
import { Avatar } from '@/design-system/components/Avatar';
import { Button } from '@/design-system/components/Button';
import { Modal } from '@/design-system/components/Modal';
import { Input } from '@/design-system/components/Input';
import { Badge } from '@/design-system/components/Badge';
import { t } from '@/lib/i18n';
import styles from './RpsRoomPage.module.css';

const CHOICE_EMOJI: Record<string, string> = { rock: '✊', paper: '✋', scissors: '✌️' };
const CHOICE_LABEL_KEY: Record<string, string> = {
  rock: 'rpsRoom.chooseRock',
  paper: 'rpsRoom.choosePaper',
  scissors: 'rpsRoom.chooseScissors',
};

function getBattleEffect(winner: string, loser: string): string {
  if (winner === 'scissors' && loser === 'paper') return '✌️ cắt ✋';
  if (winner === 'paper' && loser === 'rock') return '✋ bọc ✊';
  if (winner === 'rock' && loser === 'scissors') return '✊ đập ✌️';
  return '';
}

function useTimerMs(startedAt: number | null, timerSec: number) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!startedAt) return;
    const id = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(id);
  }, [startedAt]);
  if (!startedAt) return null;
  return Math.max(0, timerSec * 1000 - (now - startedAt));
}

export default function RpsRoomPage() {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const myId = sessionStore((s) => s.user?.id);

  const passwordFromState = (location.state as { password?: string } | null)?.password;
  const [showPasswordModal, setShowPasswordModal] = useState(!passwordFromState);
  const [passwordInput, setPasswordInput] = useState('');
  const [wrongPassword, setWrongPassword] = useState(false);

  const join = useRpsRoomJoin();
  const leave = useRpsRoomLeave();
  const setReady = useRpsRoomReady();
  const choose = useRpsRoomChoose();
  const sendChat = useRpsRoomChat();

  useRpsRoomSocket(roomId ?? null, (code) => {
    if (code === 'WRONG_PASSWORD') {
      setWrongPassword(true);
      setShowPasswordModal(true);
    }
  });

  const room_id = rpsRoomStore((s) => s.room_id);
  const name = rpsRoomStore((s) => s.name);
  const status = rpsRoomStore((s) => s.status);
  const matchStarted = rpsRoomStore((s) => s.matchStarted);
  const role = rpsRoomStore((s) => s.role);
  const player1 = rpsRoomStore((s) => s.player1);
  const player2 = rpsRoomStore((s) => s.player2);
  const spectatorCount = rpsRoomStore((s) => s.spectatorCount);
  const readyIds = rpsRoomStore((s) => s.readyIds);
  const winsNeeded = rpsRoomStore((s) => s.winsNeeded);
  const maxGames = rpsRoomStore((s) => s.maxGames);
  const timeLimitSec = rpsRoomStore((s) => s.timeLimitSec);
  const gameNumber = rpsRoomStore((s) => s.gameNumber);
  const gameStartedAt = rpsRoomStore((s) => s.gameStartedAt);
  const player1GamesWon = rpsRoomStore((s) => s.player1GamesWon);
  const player2GamesWon = rpsRoomStore((s) => s.player2GamesWon);
  const chosenIds = rpsRoomStore((s) => s.chosenIds);
  const myChoiceSubmitted = rpsRoomStore((s) => s.myChoiceSubmitted);
  const lastReveal = rpsRoomStore((s) => s.lastReveal);
  const revealHistory = rpsRoomStore((s) => s.revealHistory);
  const matchEnd = rpsRoomStore((s) => s.matchEnd);
  const closeReason = rpsRoomStore((s) => s.closeReason);
  const myEmployeeId = rpsRoomStore((s) => s.myEmployeeId);
  const chat = rpsRoomStore((s) => s.chat);

  // Join lúc mount (dùng password truyền qua state nếu có)
  useEffect(() => {
    if (!roomId) return;
    if (passwordFromState) {
      join(roomId, passwordFromState);
    }
    return () => {
      rpsRoomStore.getState().reset();
    };
  }, [roomId]);

  // Màn hình "Fighting" + đếm ngược 5s khi cả 2 người chơi vừa cùng sẵn sàng (trận bắt đầu)
  const [showFight, setShowFight] = useState(false);
  const [fightCount, setFightCount] = useState(5);
  const prevMatchStartedRef = useRef(false);

  useEffect(() => {
    if (matchStarted && !prevMatchStartedRef.current) {
      setFightCount(5);
      setShowFight(true);
    }
    prevMatchStartedRef.current = matchStarted;
  }, [matchStarted]);

  useEffect(() => {
    if (!showFight) return;
    if (fightCount <= 0) {
      const hideTimer = setTimeout(() => setShowFight(false), 600);
      return () => clearTimeout(hideTimer);
    }
    const tickTimer = setTimeout(() => setFightCount((c) => c - 1), 1000);
    return () => clearTimeout(tickTimer);
  }, [showFight, fightCount]);

  // Hiệu ứng tung hoa chúc mừng người thắng khi trận đấu vừa kết thúc
  const [showCelebrate, setShowCelebrate] = useState(false);
  const prevMatchEndRef = useRef<typeof matchEnd>(null);

  useEffect(() => {
    if (matchEnd && !prevMatchEndRef.current) {
      setShowCelebrate(true);
    }
    prevMatchEndRef.current = matchEnd;
  }, [matchEnd]);

  // Hiệu ứng "lắc búa" 1-2-3 trước khi lộ kết quả mỗi hiệp (full màn hình)
  const [shakePhase, setShakePhase] = useState<'idle' | 'shake' | 'reveal'>('idle');
  const [shakeBeat, setShakeBeat] = useState(1);
  const prevRevealRef = useRef<typeof lastReveal>(null);

  useEffect(() => {
    if (lastReveal && !prevRevealRef.current) {
      setShakeBeat(1);
      setShakePhase('shake');
    }
    prevRevealRef.current = lastReveal;
  }, [lastReveal]);

  useEffect(() => {
    if (shakePhase !== 'shake') return;
    if (shakeBeat > 3) {
      setShakePhase('reveal');
      return;
    }
    const timer = setTimeout(() => setShakeBeat((b) => b + 1), 550);
    return () => clearTimeout(timer);
  }, [shakePhase, shakeBeat]);

  useEffect(() => {
    if (shakePhase !== 'reveal') return;
    const timer = setTimeout(() => setShakePhase('idle'), 5000);
    return () => clearTimeout(timer);
  }, [shakePhase]);

  if (!roomId) return null;

  const submitJoin = (spectateOnly: boolean) => {
    setWrongPassword(false);
    join(roomId, spectateOnly ? undefined : passwordInput.trim() || undefined, spectateOnly);
    setShowPasswordModal(false);
  };

  const handlePasswordFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitJoin(false);
  };

  const handleLeave = () => {
    leave(roomId);
    rpsRoomStore.getState().reset();
    navigate('/rps-rooms');
  };

  const isPlayer1 = role === 'player' && myEmployeeId === player1?.employee_id;
  const isPlayer2 = role === 'player' && myEmployeeId === player2?.employee_id;
  const isPlayer = isPlayer1 || isPlayer2;
  const iAmReady = !!myEmployeeId && readyIds.includes(myEmployeeId);
  const bothPlayersPresent = !!player1 && !!player2;

  const remainMs = useTimerMs(status === 'playing' ? gameStartedAt : null, timeLimitSec);
  const remainSec = remainMs !== null ? Math.ceil(remainMs / 1000) : null;
  const timerPct = remainMs !== null ? remainMs / (timeLimitSec * 1000) : 1;

  const hasReveal = !!lastReveal;
  const canChoose = isPlayer && !myChoiceSubmitted && status === 'playing' && !hasReveal && !matchEnd;

  const winnerInfo = matchEnd
    ? matchEnd.winner_id === player1?.employee_id
      ? player1
      : matchEnd.winner_id === player2?.employee_id
        ? player2
        : null
    : null;

  return (
    <div className={styles.root}>
      {showFight && (
        <FightingOverlay player1={player1} player2={player2} count={fightCount} />
      )}
      {showCelebrate && matchEnd && (
        <MatchEndCelebration
          winnerName={winnerInfo?.display_name ?? t('rpsRoom.playerBadge')}
          onDone={() => setShowCelebrate(false)}
        />
      )}
      {shakePhase !== 'idle' && lastReveal && (
        <ShakeRevealOverlay phase={shakePhase} beat={shakeBeat} reveal={lastReveal} player1={player1} player2={player2} />
      )}
      <div className={styles.layout}>
        <div className={styles.main}>
          {/* Header */}
          <div className={styles.header}>
            <div>
              <h1 className={styles.title}>{name || '…'}</h1>
              <div className={styles.subRow}>
                <Badge tone={status === 'playing' ? 'warning' : status === 'ended' ? 'neutral' : 'success'}>
                  {status === 'waiting' ? t('rpsRoom.statusWaiting') : status === 'playing' ? t('rpsRoom.statusPlaying') : t('rpsRoom.statusEnded')}
                </Badge>
                <span className={styles.metaItem}><Swords size={13} />{t('rpsRoom.maxGames', { n: maxGames })}</span>
                <span className={styles.metaItem}><Users size={13} />{t('rpsRoom.spectatorCount', { n: spectatorCount })}</span>
              </div>
            </div>
            <Button variant="secondary" leftIcon={<LogOut size={15} />} onClick={handleLeave}>
              {t('rpsRoom.leaveRoom')}
            </Button>
          </div>

          {/* Players VS */}
          <div className={styles.vsCard}>
            <div className={styles.vsRow}>
              <PlayerSlot
                info={player1}
                isMe={isPlayer1}
                score={player1GamesWon}
                isReady={!!player1 && readyIds.includes(player1.employee_id)}
                hasChosen={!!player1 && chosenIds.includes(player1.employee_id) && !hasReveal}
                showReadyDot={status === 'waiting'}
                showChoiceDot={status === 'playing'}
                isWinner={!!matchEnd && matchEnd.winner_id === player1?.employee_id}
              />
              <div className={styles.vsSep}>
                {matchEnd ? (
                  <Trophy size={28} color="var(--color-warning)" />
                ) : (
                  <span className={styles.vsText}>VS</span>
                )}
                {status === 'playing' && !matchEnd && (
                  <div className={styles.gameLabel}>{t('rpsRoom.gameNumber', { n: gameNumber, total: maxGames })}</div>
                )}
                {status === 'playing' && remainSec !== null && !matchEnd && (
                  <div className={styles.timerWrap}>
                    <div
                      className={styles.timerBar}
                      style={{ width: `${Math.round(timerPct * 100)}%`, background: timerPct < 0.3 ? 'var(--color-danger)' : 'var(--color-success)' }}
                    />
                    <span className={styles.timerText}>{remainSec}s</span>
                  </div>
                )}
              </div>
              <PlayerSlot
                info={player2}
                isMe={isPlayer2}
                score={player2GamesWon}
                isReady={!!player2 && readyIds.includes(player2.employee_id)}
                hasChosen={!!player2 && chosenIds.includes(player2.employee_id) && !hasReveal}
                showReadyDot={status === 'waiting'}
                showChoiceDot={status === 'playing'}
                isWinner={!!matchEnd && matchEnd.winner_id === player2?.employee_id}
                emptyLabel={t('rpsRoom.waitingForPlayer')}
              />
            </div>

            {/* Waiting room: ready button */}
            {status === 'waiting' && isPlayer && !matchEnd && (
              <div className={styles.readyRow}>
                {bothPlayersPresent ? (
                  <Button
                    variant={iAmReady ? 'secondary' : 'primary'}
                    onClick={() => setReady(roomId)}
                  >
                    {iAmReady ? t('rpsRoom.cancelReady') : t('rpsRoom.ready')}
                  </Button>
                ) : (
                  <div className={styles.hint}>{t('rpsRoom.waitingForPlayer')}</div>
                )}
                {bothPlayersPresent && iAmReady && (
                  <div className={styles.hint}>{t('rpsRoom.waitingOtherReady')}</div>
                )}
              </div>
            )}

            {/* Lịch sử kết quả từng hiệp (ẩn hiệp đang hiển thị full màn hình lúc lắc búa) */}
            {revealHistory.length > 0 && (
              <div className={styles.revealHistory}>
                {revealHistory
                  .filter((r) => shakePhase === 'idle' || r.game_number !== lastReveal?.game_number)
                  .map((r) => (
                    <div key={r.game_number} className={styles.revealHistoryItem}>
                      <div className={styles.revealHistoryLabel}>{t('rpsRoom.gameNumber', { n: r.game_number, total: maxGames })}</div>
                      <RevealDisplay reveal={r} player1={player1} player2={player2} />
                    </div>
                  ))}
              </div>
            )}

            {/* Choice buttons */}
            {canChoose && (
              <div className={styles.choiceRow}>
                <div className={styles.choiceBtns}>
                  {(['rock', 'paper', 'scissors'] as const).map((c) => (
                    <button key={c} className={styles.choiceBtn} onClick={() => choose(roomId, gameNumber, c)}>
                      <span className={styles.choiceEmoji}>{CHOICE_EMOJI[c]}</span>
                      <span className={styles.choiceLabel}>{t(CHOICE_LABEL_KEY[c])}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isPlayer && myChoiceSubmitted && !hasReveal && status === 'playing' && !matchEnd && (
              <div className={styles.waitingNote}>✅ {t('rpsRoom.chosen')} — {t('rpsRoom.waitingChoice')}</div>
            )}

            {/* Match end */}
            {matchEnd && (
              <div className={styles.endCard}>
                <div className={styles.endTitle}>
                  <Trophy size={20} color="var(--color-warning)" />
                  {t('rpsRoom.matchEndedTitle')}
                </div>
                <div className={styles.endScore}>
                  {t('rpsRoom.matchScore', { p1: matchEnd.player1_games_won, p2: matchEnd.player2_games_won })}
                </div>
                {isPlayer && (
                  <div className={styles.endResult}>
                    {matchEnd.winner_id === myEmployeeId ? `🏆 ${t('rpsRoom.youWin')}` : `${t('rpsRoom.youLose')}`}
                  </div>
                )}
                {matchEnd.reason === 'opponent_left' && (
                  <div className={styles.hint}>{t('rpsRoom.reasonOpponentLeft')}</div>
                )}
                <Button onClick={() => navigate('/rps-rooms')}>{t('rpsRoom.backToList')}</Button>
              </div>
            )}

            {closeReason && !matchEnd && (
              <div className={styles.endCard}>
                <div className={styles.endTitle}>{t('rpsRoom.roomClosed')}</div>
                {closeReason === 'closed_by_owner' && <div className={styles.hint}>{t('rpsRoom.closedByOwner')}</div>}
                <Button onClick={() => navigate('/rps-rooms')}>{t('rpsRoom.backToList')}</Button>
              </div>
            )}
          </div>
        </div>

        <div className={styles.sidePanel}>
          <RoomChat
            chat={chat}
            myId={myId}
            onSend={(msg) => sendChat(roomId, msg)}
          />
        </div>
      </div>

      {/* Password / spectate modal */}
      <Modal
        open={showPasswordModal}
        onOpenChange={(open) => {
          setShowPasswordModal(open);
          if (!open && !room_id) {
            // người dùng đóng modal mà chưa join -> vào với vai trò khán giả
            join(roomId, undefined, true);
          }
        }}
        title={t('rpsRoom.joinModalTitle')}
      >
        <form onSubmit={handlePasswordFormSubmit} className={styles.passwordForm}>
          <p className={styles.hint}>{t('rpsRoom.joinHint', { name: name || '' })}</p>
          <Input
            placeholder={t('rpsRoom.passwordOptionalPlaceholder')}
            value={passwordInput}
            onChange={(e) => { setPasswordInput(e.target.value); setWrongPassword(false); }}
            autoFocus
            maxLength={20}
            type="password"
          />
          {wrongPassword && <span className={styles.errorText}>{t('rpsRoom.wrongPassword')}</span>}
          <div className={styles.passwordActions}>
            <Button variant="secondary" type="button" onClick={() => submitJoin(true)}>
              {t('rpsRoom.enterAsSpectator')}
            </Button>
            <Button type="submit">{t('rpsRoom.enterAsPlayer')}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

type FightOverlayPlayer = { employee_id: string; display_name: string | null; avatar_url: string | null } | null;

function FightingOverlay({
  player1,
  player2,
  count,
}: {
  player1: FightOverlayPlayer;
  player2: FightOverlayPlayer;
  count: number;
}) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 30);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className={`${styles.fightOverlay} ${visible ? styles.fightOverlayVisible : ''}`}>
      <div className={styles.fightSideRed} />
      <div className={styles.fightSideBlue} />

      <div className={styles.fightContent}>
        <div className={styles.fightTitle}>{t('rpsRoom.fightingTitle')}</div>

        <div className={styles.fightVsRow}>
          <div className={styles.fightPlayer}>
            <Avatar src={player1?.avatar_url ?? undefined} name={player1?.display_name ?? '?'} size="lg" />
            <div className={styles.fightPlayerName}>{player1?.display_name ?? '?'}</div>
          </div>

          <div className={styles.fightCenter}>
            <div className={styles.fightVs}>VS</div>
            <div key={count} className={styles.fightCountdown}>
              {count > 0 ? count : t('rpsRoom.fightGo')}
            </div>
          </div>

          <div className={styles.fightPlayer}>
            <Avatar src={player2?.avatar_url ?? undefined} name={player2?.display_name ?? '?'} size="lg" />
            <div className={styles.fightPlayerName}>{player2?.display_name ?? '?'}</div>
          </div>
        </div>

        <div className={styles.fightSubTitle}>{t('rpsRoom.fightSubTitle')}</div>
      </div>
    </div>
  );
}

function MatchEndCelebration({ winnerName, onDone }: { winnerName: string; onDone: () => void }) {
  const [visible, setVisible] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const showTimer = setTimeout(() => setVisible(true), 30);
    const doneTimer = setTimeout(onDone, 3800);
    return () => { clearTimeout(showTimer); clearTimeout(doneTimer); };
  }, [onDone]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const colors = ['#facc15', '#4ade80', '#38bdf8', '#f43f5e', '#a78bfa', '#fb923c'];
    const pieces = Array.from({ length: 130 }, () => ({
      x: Math.random() * canvas.width,
      y: -10 - Math.random() * 140,
      vx: (Math.random() - 0.5) * 5,
      vy: 3 + Math.random() * 5,
      color: colors[Math.floor(Math.random() * colors.length)],
      w: 7 + Math.random() * 7,
      h: 4 + Math.random() * 4,
      rot: Math.random() * Math.PI * 2,
      rotV: (Math.random() - 0.5) * 0.18,
    }));

    let id: number;
    const tick = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const p of pieces) {
        p.x += p.vx; p.y += p.vy; p.vy += 0.1; p.rot += p.rotV;
        ctx.save();
        ctx.globalAlpha = 0.9;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }
      if (pieces.some((p) => p.y < canvas.height + 20)) id = requestAnimationFrame(tick);
    };
    id = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <div className={`${styles.celebrateOverlay} ${visible ? styles.celebrateOverlayVisible : ''}`}>
      <canvas ref={canvasRef} className={styles.celebrateCanvas} />
      <div className={styles.celebrateContent}>
        <div className={styles.celebrateEmoji}>🏆</div>
        <div className={styles.celebrateTitle}>{t('rpsRoom.celebrateWinner', { name: winnerName })}</div>
      </div>
    </div>
  );
}

function PlayerSlot({
  info,
  isMe,
  score,
  isReady,
  hasChosen,
  showReadyDot,
  showChoiceDot,
  isWinner,
  emptyLabel,
}: {
  info: { employee_id: string; display_name: string | null; avatar_url: string | null } | null;
  isMe: boolean;
  score: number;
  isReady: boolean;
  hasChosen: boolean;
  showReadyDot: boolean;
  showChoiceDot: boolean;
  isWinner: boolean;
  emptyLabel?: string;
}) {
  return (
    <div className={`${styles.vsPlayer} ${isWinner ? styles.vsWinner : ''}`}>
      {info ? (
        <>
          <Avatar src={info.avatar_url ?? undefined} name={info.display_name ?? '?'} size="lg" />
          <div className={styles.vsName}>
            {info.display_name ?? '?'}
            {isMe && <span className={styles.youTag}>{t('rpsRoom.you')}</span>}
          </div>
          <div className={styles.vsScore}>{score}</div>
          {showReadyDot && isReady && <div className={styles.statusDot} title={t('rpsRoom.ready')} />}
          {showChoiceDot && hasChosen && <div className={styles.statusDot} title={t('rpsRoom.chosen')} />}
        </>
      ) : (
        <>
          <div className={styles.emptySlot} />
          <div className={styles.vsName}>{emptyLabel ?? '—'}</div>
        </>
      )}
    </div>
  );
}

type ShakeRevealInfo = { display_name: string | null } | null;
type ShakeRevealData = { game_number: number; player1_choice: string; player2_choice: string; result: string; is_random_tiebreak: boolean };

function ShakeRevealOverlay({
  phase,
  beat,
  reveal,
  player1,
  player2,
}: {
  phase: 'shake' | 'reveal';
  beat: number;
  reveal: ShakeRevealData;
  player1: ShakeRevealInfo;
  player2: ShakeRevealInfo;
}) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 30);
    return () => clearTimeout(t);
  }, []);

  const p1Win = reveal.result === 'player1_wins';
  const p2Win = reveal.result === 'player2_wins';
  const isDraw = reveal.result === 'draw';
  const winnerChoice = p1Win ? reveal.player1_choice : p2Win ? reveal.player2_choice : null;
  const loserChoice = p1Win ? reveal.player2_choice : p2Win ? reveal.player1_choice : null;
  const battleText = winnerChoice && loserChoice ? getBattleEffect(winnerChoice, loserChoice) : null;

  return (
    <div className={`${styles.shakeOverlay} ${visible ? styles.shakeOverlayVisible : ''}`}>
      <div className={styles.shakeContent}>
        {phase === 'shake' ? (
          <>
            <div className={styles.shakeRow}>
              <div className={styles.shakeFistWrap}>
                <span key={`p1-${beat}`} className={styles.shakeFist}>✊</span>
                <span className={styles.shakeName}>{player1?.display_name ?? '—'}</span>
              </div>
              <div key={`beat-${beat}`} className={styles.shakeBeatNumber}>{beat <= 3 ? beat : ''}</div>
              <div className={styles.shakeFistWrap}>
                <span key={`p2-${beat}`} className={styles.shakeFist}>✊</span>
                <span className={styles.shakeName}>{player2?.display_name ?? '—'}</span>
              </div>
            </div>
            <div className={styles.shakeHint}>{t('rpsRoom.shakeHint')}</div>
          </>
        ) : (
          <>
            <div className={styles.shakeRow}>
              <div className={`${styles.shakeRevealPlayer} ${p1Win ? styles.revealWinner : p2Win ? styles.revealLoser : styles.revealDraw}`}>
                <span className={styles.shakeRevealEmoji}>{CHOICE_EMOJI[reveal.player1_choice]}</span>
                <span className={styles.shakeName}>{player1?.display_name ?? '—'}</span>
                {p1Win && <span className={styles.revealBadge} data-result="win">{t('rpsRoom.youWin')}</span>}
                {p2Win && <span className={styles.revealBadge} data-result="lose">{t('rpsRoom.youLose')}</span>}
                {isDraw && <span className={styles.revealBadge} data-result="draw">{t('rpsRoom.draw')}</span>}
              </div>
              <div className={styles.shakeVsCenter}>
                {battleText ? <span className={styles.battleText}>{battleText}</span> : <span className={styles.revealVs}>{isDraw ? '🤝' : '⚡'}</span>}
              </div>
              <div className={`${styles.shakeRevealPlayer} ${p2Win ? styles.revealWinner : p1Win ? styles.revealLoser : styles.revealDraw}`}>
                <span className={styles.shakeRevealEmoji}>{CHOICE_EMOJI[reveal.player2_choice]}</span>
                <span className={styles.shakeName}>{player2?.display_name ?? '—'}</span>
                {p2Win && <span className={styles.revealBadge} data-result="win">{t('rpsRoom.youWin')}</span>}
                {p1Win && <span className={styles.revealBadge} data-result="lose">{t('rpsRoom.youLose')}</span>}
                {isDraw && <span className={styles.revealBadge} data-result="draw">{t('rpsRoom.draw')}</span>}
              </div>
            </div>
            {reveal.is_random_tiebreak && <div className={styles.tiebreakNote}>* Quyết định ngẫu nhiên</div>}
          </>
        )}
      </div>
    </div>
  );
}

function RevealDisplay({
  reveal,
  player1,
  player2,
}: {
  reveal: { game_number: number; player1_choice: string; player2_choice: string; result: string; is_random_tiebreak: boolean };
  player1: { display_name: string | null } | null;
  player2: { display_name: string | null } | null;
}) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    setVisible(false);
    const timer = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(timer);
  }, [reveal.game_number, reveal.result]);

  const p1Win = reveal.result === 'player1_wins';
  const p2Win = reveal.result === 'player2_wins';
  const isDraw = reveal.result === 'draw';
  const winnerChoice = p1Win ? reveal.player1_choice : p2Win ? reveal.player2_choice : null;
  const loserChoice = p1Win ? reveal.player2_choice : p2Win ? reveal.player1_choice : null;
  const battleText = winnerChoice && loserChoice ? getBattleEffect(winnerChoice, loserChoice) : null;

  return (
    <div className={`${styles.revealRow} ${visible ? styles.revealRowVisible : ''}`}>
      <div className={`${styles.revealPlayer} ${p1Win ? styles.revealWinner : p2Win ? styles.revealLoser : styles.revealDraw}`}>
        <span className={styles.revealEmoji}>{CHOICE_EMOJI[reveal.player1_choice]}</span>
        <span className={styles.revealName}>{player1?.display_name ?? '—'}</span>
        {p1Win && <span className={styles.revealBadge} data-result="win">{t('rpsRoom.youWin')}</span>}
        {p2Win && <span className={styles.revealBadge} data-result="lose">{t('rpsRoom.youLose')}</span>}
        {isDraw && <span className={styles.revealBadge} data-result="draw">{t('rpsRoom.draw')}</span>}
      </div>
      <div className={styles.revealCenter}>
        {battleText ? <span className={styles.battleText}>{battleText}</span> : <span className={styles.revealVs}>{isDraw ? '🤝' : '⚡'}</span>}
      </div>
      <div className={`${styles.revealPlayer} ${p2Win ? styles.revealWinner : p1Win ? styles.revealLoser : styles.revealDraw}`}>
        <span className={styles.revealEmoji}>{CHOICE_EMOJI[reveal.player2_choice]}</span>
        <span className={styles.revealName}>{player2?.display_name ?? '—'}</span>
        {p2Win && <span className={styles.revealBadge} data-result="win">{t('rpsRoom.youWin')}</span>}
        {p1Win && <span className={styles.revealBadge} data-result="lose">{t('rpsRoom.youLose')}</span>}
        {isDraw && <span className={styles.revealBadge} data-result="draw">{t('rpsRoom.draw')}</span>}
      </div>
      {reveal.is_random_tiebreak && <div className={styles.tiebreakNote}>* Quyết định ngẫu nhiên</div>}
    </div>
  );
}

function RoomChat({
  chat,
  myId,
  onSend,
}: {
  chat: { id: string; sender_id: string; sender_name: string | null; sender_avatar: string | null; is_player: boolean; message: string; sent_at: number }[];
  myId: string | undefined;
  onSend: (msg: string) => void;
}) {
  const [input, setInput] = useState('');
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [chat]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    onSend(input.trim());
    setInput('');
    inputRef.current?.focus();
  };

  return (
    <div className={styles.chatRoot}>
      <div className={styles.chatHeader}>
        <MessageCircle size={14} />
        <span>{t('rpsRoom.chatTitle')}</span>
      </div>
      <div className={styles.chatList} ref={listRef}>
        {chat.length === 0 ? (
          <div className={styles.chatEmpty}>{t('chat.empty')}</div>
        ) : (
          chat.map((msg) => {
            const isMe = msg.sender_id === myId;
            return (
              <div key={msg.id} className={`${styles.msgRow} ${isMe ? styles.msgRowMe : ''}`}>
                {!isMe && (
                  <Avatar src={msg.sender_avatar ?? undefined} name={msg.sender_name ?? '?'} size="sm" />
                )}
                <div className={`${styles.bubble} ${isMe ? styles.bubbleMe : ''} ${msg.is_player ? styles.bubblePlayer : ''}`}>
                  {!isMe && (
                    <span className={styles.senderName}>
                      {msg.sender_name ?? t('chat.unknownSender')}
                      {msg.is_player && <span className={styles.playerBadge}>{t('rpsRoom.playerBadge')}</span>}
                    </span>
                  )}
                  <span className={styles.msgText}>{msg.message}</span>
                  <span className={styles.msgTime}>
                    {new Date(msg.sent_at).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
      <form className={styles.chatInputRow} onSubmit={handleSubmit}>
        <input
          ref={inputRef}
          className={styles.chatInput}
          placeholder={t('rpsRoom.chatPlaceholder')}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          maxLength={200}
          autoComplete="off"
        />
        <button type="submit" className={styles.chatSendBtn} disabled={!input.trim()}>
          <Send size={14} />
        </button>
      </form>
    </div>
  );
}
